import React, { createContext, Reducer, useContext, useReducer } from 'react';
import { AlertComponentProps, AlertProviderProps, Provider as AlertProvider, useAlert } from 'react-alert';
import { ISmartAlertContentProp, SmartAlertContent, SmartAlertTemplate } from './SmartAlertTemplate';

type SmartAlertKey = string;
const getSmartAlertKey = (text: ISmartAlertContentProp['text'], icon?: ISmartAlertContentProp['icon']): SmartAlertKey => {
  return `${text}${icon ? `-${icon}` : ''}`;
};
interface SmartAlertState {
  alerts: { [smartAlertKey: string]: AlertComponentProps };
}
interface SmartAlertAction {
  addAlert: (smartAlertKey: SmartAlertKey, alertComponent: AlertComponentProps) => void;
  removeAlert: (smartAlertKey: SmartAlertKey) => void;
}
type Action =
  | { type: 'ADD_ALERT'; smartAlertKey: SmartAlertKey; alertComponent: AlertComponentProps }
  | { type: 'REMOVE_ALERT'; smartAlertKey: SmartAlertKey };

const smartAlertCReducer: Reducer<SmartAlertState, Action> = (state, action) => {
  switch (action.type) {
    case 'ADD_ALERT':
      return { ...state, alerts: { ...state.alerts, [action.smartAlertKey]: action.alertComponent } };
    case 'REMOVE_ALERT':
      const alerts = { ...state.alerts };
      if (alerts[action.smartAlertKey]) {
        const alertComponent = alerts[action.smartAlertKey];
        alertComponent.close();
        // setTimeout(() => {
        //   alertComponent.close();
        // }, 500);
        delete alerts[action.smartAlertKey];
      }
      return { ...state, alerts };
    default:
      return state;
  }
};

const SmartAlertContext = createContext<SmartAlertState & SmartAlertAction>({alerts: {}, addAlert: () => {}, removeAlert: () => {}});
type SmartAlertProviderProps = Omit<AlertProviderProps, 'template'>;
export const SmartAlertProvider: React.FC<SmartAlertProviderProps> = ({
  children,
  ...alertOptions
}: React.PropsWithChildren<SmartAlertProviderProps>) => {
  const [smartAlertState, smartAlertDispatch] = useReducer(smartAlertCReducer, {
    alerts: {},
  });
  return (
    <SmartAlertContext.Provider
      value={{
        ...smartAlertState,
        addAlert: (smartAlertKey, alertComponent) => {
          smartAlertDispatch({ type: 'ADD_ALERT', smartAlertKey, alertComponent });
        },
        removeAlert: (smartAlertKey) => {
          smartAlertDispatch({ type: 'REMOVE_ALERT', smartAlertKey });
        },
      }}
    >
      <AlertProvider template={SmartAlertTemplate} {...alertOptions}>
        {children}
      </AlertProvider>
    </SmartAlertContext.Provider>
  );
};

export const useSmartAlert = () => {
  const alert = useAlert();
  const { addAlert, removeAlert } = useContext(SmartAlertContext);
  return {
    show: (
      text: ISmartAlertContentProp['text'],
      icon?: ISmartAlertContentProp['icon'],
      color?: ISmartAlertContentProp['color'],
      inactiveIcon?: ISmartAlertContentProp['inactiveIcon'],
      onUndoClick?: ISmartAlertContentProp['onUndoClick'],
      onClosed?: ISmartAlertContentProp['onClosed'],
    ) => {
      const key = getSmartAlertKey(text, icon);
      const alertComponent = alert.show(<SmartAlertContent icon={icon} text={text} color={color} inactiveIcon={inactiveIcon} onUndoClick={onUndoClick} onClosed={onClosed} />);
      removeAlert(key);
      addAlert(key, alertComponent);
    },
  };
};
